import { ChevronRight, Sparkles } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { completeOnboarding } from "@/features/onboarding/api";
import { ONBOARDING_STEPS, type OnboardingStepMeta } from "@/features/onboarding/steps-meta";
import {
  onboardingProgressSummary,
  useOnboardingProgressStore,
} from "@/features/onboarding/store/onboarding-progress-store";
import { useStepWizard } from "@/hooks/use-step-wizard";
import { EncouragementStrip, ProgressRing, UnlockCard } from "./hub/hub-pieces";
import { StepList } from "./hub/step-list";
import { BrandingStep } from "./steps/branding-step";
import { CompanyDetailsStep } from "./steps/company-details-step";
import { EServicesStep } from "./steps/eservices-step";
import { InviteTeamStep } from "./steps/invite-team-step";
import { PortalStep } from "./steps/portal-step";
import { SignatureLetterheadStep } from "./steps/signature-letterhead-step";
import type { OnboardingStepProps } from "./steps/types";

const STEP_COMPONENTS: ((p: OnboardingStepProps) => ReactElement)[] = [
  CompanyDetailsStep,
  BrandingStep,
  SignatureLetterheadStep,
  PortalStep,
  EServicesStep,
  InviteTeamStep,
];

/**
 * The firm "Getting started" hub — a hero + progress ring, a left step list
 * (all six steps with done/current/skipped states), the active step's detail
 * panel, and an encouragement strip. Rendered inside the app shell (AppLayout).
 */
export function OnboardingHub() {
  const navigate = useNavigate();
  const firmName = useAuthStore((s) => s.user?.firm?.name) ?? "";
  const { step, isFirst, next, back, goto } = useStepWizard(ONBOARDING_STEPS.length);
  const [completing, setCompleting] = useState(false);

  const { done, doneCaptions, skipped, load, refresh, markSkipped, unmarkSkipped } =
    useOnboardingProgressStore(
      useShallow((s) => ({
        done: s.done,
        doneCaptions: s.doneCaptions,
        skipped: s.skipped,
        load: s.load,
        refresh: s.refresh,
        markSkipped: s.markSkipped,
        unmarkSkipped: s.unmarkSkipped,
      })),
    );

  useEffect(() => {
    load();
  }, [load]);

  const { doneCount, skippedCount, percent } = onboardingProgressSummary({ done, skipped });
  const minutesLeft = ONBOARDING_STEPS.reduce(
    (sum, s, i) => (done[i] ? sum : sum + s.minutes),
    0,
  );

  const complete = async (to: string) => {
    setCompleting(true);
    try {
      await completeOnboarding();
      navigate(to, { replace: true });
    } catch {
      toast.error("Couldn't finish setup. Please try again.");
      setCompleting(false);
    }
  };

  const Step = STEP_COMPONENTS[step]!;
  const meta = ONBOARDING_STEPS[step] as OnboardingStepMeta;

  return (
    <div className="relative flex flex-col gap-6">
      {/* Decorative warm/cool glow, matching the mockup (token-driven). */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-24 size-[480px] rounded-full opacity-[0.1]"
        style={{ background: "radial-gradient(closest-side, var(--primary), transparent)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-40 -left-32 size-[420px] rounded-full opacity-[0.08]"
        style={{ background: "radial-gradient(closest-side, var(--info), transparent)" }}
      />

      {/* Finish-later escape hatch (the app header is provided by AppLayout). */}
      <div className="relative flex justify-end">
        <Button
          variant="ghost"
          onClick={() => complete("/dashboard")}
          disabled={completing}
          className="text-muted-foreground"
        >
          {completing ? "Finishing…" : "I'll finish later"}
          <ChevronRight className="size-4" />
        </Button>
      </div>

      {/* Hero */}
      <section className="relative flex flex-wrap items-end justify-between gap-5">
        <div className="flex max-w-2xl flex-col gap-2">
          <span className="bg-warning-soft text-warning inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold tracking-[0.06em] uppercase">
            <Sparkles className="size-3.5" /> Day one
          </span>
          <h1 className="text-[2.5rem] leading-[1.05] tracking-tight">
            Let's make this place feel like{" "}
            <span className="text-primary">{firmName || "your firm"}.</span>
          </h1>
          <p className="text-muted-foreground mt-1 text-[15px]">
            Six short steps — your letters, portal and IRS connection will carry your name from the
            very first client. You can skip anything and come back; we'll keep your place.
          </p>
        </div>
        <Card className="w-fit flex-row items-center gap-4 p-4">
          <ProgressRing percent={percent} />
          <span className="flex flex-col leading-tight">
            <span className="font-semibold">{doneCount} of 6 done</span>
            <span className="text-muted-foreground text-xs">
              about {minutesLeft} minute{minutesLeft === 1 ? "" : "s"} left
              {skippedCount > 0 ? ` · ${skippedCount} skipped` : ""}
            </span>
          </span>
        </Card>
      </section>

      {/* Body: step list + active step detail */}
      <section className="relative grid items-start gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <StepList
            steps={ONBOARDING_STEPS}
            current={step}
            done={done}
            skipped={skipped}
            doneCaptions={doneCaptions}
            onSelect={goto}
            footer={<UnlockCard onUnlock={() => complete("/clients/new")} />}
          />
        </div>
        <div className="lg:col-span-2">
          <Step
            meta={meta}
            index={step}
            isFirst={isFirst}
            onBack={back}
            onSkip={() => {
              markSkipped(step);
              next();
            }}
            onSaved={() => {
              unmarkSkipped(step);
              refresh();
              next();
            }}
          />
        </div>
      </section>

      <EncouragementStrip />
    </div>
  );
}
